import React, {
    HTMLInputAutoCompleteAttribute,
    HTMLInputTypeAttribute
} from "react";
import {
    Controller,
    FieldValues,
    Path,
    PathValue,
    useFormContext
} from "react-hook-form";

import { MenuComponent } from "@/components/Form/Base/components/selectMenu.component";
import { MenuListComponent } from "@/components/Form/Base/components/selectMenuList.component";
import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { FormText } from "@/components/Form/Base/formText.component";
import { Input } from "@/components/Form/Base/input.component";
import { InputGroup } from "@/components/Form/Base/inputGroup.component";
import { InputMask } from "@/components/Form/Base/maskedInput.component";
import { MicroCopy } from "@/components/Form/Base/microCopy.component";
import { Select } from "@/components/Form/Base/select.component";
import { MaskedPrefixInput } from "@/components/Form/RHF/FormFields/PrefixedInput/components/maskedPrefixInput";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { FormFeedback } from "../../FormElements/formFeedback.component";
import { calculateFieldState, getFieldValue } from "../../Utils/form.helpers";
import { useAutofill } from "../../Utils/useAutofill";
import { FormValuesExploded } from "../formField.types";
import { BaseOption } from "../SelectField/selectField.component";

type PrefixedInputFieldProps<
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
> = FormValuesExploded<T> & {
    type?: HTMLInputTypeAttribute;
    prefixName: Path<T>;
    prefixOptions: Array<TOption>;
    autoComplete?: HTMLInputAutoCompleteAttribute;
    disabled?: boolean;
    renderOption: (option: any) => any;
    placeholder?: string;
    inputMaskBasedOnPrefix?: (value: string) => InputMask;
};

const PrefixedInputField = <
    T extends FieldValues,
    TOption extends BaseOption<PathValue<T, Path<T>>>
>({
    name,
    label,
    required,
    microCopy,
    prefixName,
    type = "text",
    prefixOptions,
    disabled,
    renderOption,
    autoComplete,
    placeholder,
    inputMaskBasedOnPrefix
}: PrefixedInputFieldProps<T, TOption>) => {
    const { t } = useGranite();
    const { setValue, control, trigger } = useFormContext<T>();
    const { handleOnAutofill } = useAutofill<T>(name);

    const customStyles = {
        container: (provided: any) => ({
            ...provided,
            flex: "0 0 auto",
            width: "auto",
            maxWidth: "none"
        }),
        control: (provided: any) => ({
            ...provided,
            minHeight: "100%",
            borderBottomRightRadius: 0,
            borderTopRightRadius: 0
        }),
        menu: (provided: any) => ({
            ...provided,
            width: "auto"
        }),
        indicatorSeparator: (provided: any) => ({
            ...provided,
            display: "none"
        })
    };

    const onPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        if (e.clipboardData?.getData) {
            setValue(
                name,
                e.clipboardData
                    .getData("text/plain")
                    .replace(/\D/g, "") as PathValue<T, Path<T>>,
                { shouldTouch: true }
            );
        }
    };

    const getPrefix = (value: string) => {
        const fallback = prefixOptions[0];

        if (!value) return fallback;

        const prefix = prefixOptions.find((option) => option.value === value);

        return prefix || fallback;
    };

    return (
        <FormGroup>
            <FieldLabel
                name={name}
                label={label}
                required={required}
            />
            {microCopy && (
                <FormText
                    color="default"
                    dangerouslySetInnerHTML={{ __html: microCopy }}
                />
            )}
            {!microCopy && <MicroCopy fieldId={name} />}
            <InputGroup>
                <Controller
                    control={control}
                    name={prefixName}
                    render={({ field }) => {
                        return (
                            <Select<TOption>
                                {...field}
                                instanceId={field.name}
                                classNamePrefix="select-box"
                                value={getPrefix(field.value)}
                                isSearchable={true}
                                formatOptionLabel={renderOption}
                                noOptionsMessage={() =>
                                    t("general.selectBox.no.options")
                                }
                                options={prefixOptions}
                                styles={customStyles}
                                onChange={(option) => {
                                    if (option?.value) {
                                        setValue(field.name, option.value, {
                                            shouldTouch: true
                                        });
                                        trigger(name);
                                    }
                                }}
                                aria-label={t(
                                    "accessibility.phoneField.prefix.label"
                                )}
                                isDisabled={disabled}
                                components={{
                                    Menu: MenuComponent,
                                    MenuList: MenuListComponent
                                }}
                            />
                        );
                    }}
                />
                <Controller
                    control={control}
                    name={name}
                    render={({ field, fieldState, formState }) => {
                        const newFieldState = calculateFieldState(
                            fieldState,
                            formState
                        );

                        if (inputMaskBasedOnPrefix) {
                            return (
                                <MaskedPrefixInput<T>
                                    prefixName={prefixName}
                                    field={field}
                                    onPaste={onPaste}
                                    type={type}
                                    placeholder={placeholder}
                                    autoComplete={autoComplete}
                                    disabled={disabled}
                                    isValid={newFieldState.isValid}
                                    isInvalid={newFieldState.isInvalid}
                                    isLoading={formState.isLoading}
                                    onAutofill={handleOnAutofill}
                                    inputMaskBasedOnPrefix={
                                        inputMaskBasedOnPrefix
                                    }
                                />
                            );
                        } else {
                            return (
                                <Input
                                    {...field}
                                    value={getFieldValue(
                                        field,
                                        formState.isLoading,
                                        ""
                                    )}
                                    onPaste={onPaste}
                                    id={name}
                                    className="prefixed-input__input"
                                    type={type}
                                    placeholder={placeholder}
                                    autoComplete={autoComplete?.toString()}
                                    valid={newFieldState.isValid}
                                    invalid={newFieldState.isInvalid}
                                    disabled={disabled}
                                    onAutofill={handleOnAutofill}
                                />
                            );
                        }
                    }}
                />
            </InputGroup>
            <FormFeedback
                name={name}
                label={label}
            />
        </FormGroup>
    );
};

export { PrefixedInputField };
